import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RoleRow } from '../../core/api/types';
import { initials, roleColor } from './permissionUi';

interface Props {
  roles: RoleRow[];
  selectedId: string;
  dirtyRoleId: string | null;
  canCreate: boolean;
  disabled?: boolean;
  onSelect: (id: string) => void;
  onCreate: () => void;
}

export function RoleList({ roles, selectedId, dirtyRoleId, canCreate, disabled, onSelect, onCreate }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const visible = roles.filter((r) => !q || `${r.name} ${r.code}`.toLowerCase().includes(q));

  return (
    <aside className="perm-card perm-roles">
      <label className="perm-search">
        <SearchIcon />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('permissions.searchRoles', { defaultValue: 'Search role' })}
          aria-label={t('permissions.searchRoles', { defaultValue: 'Search role' })}
        />
      </label>
      <ul className="perm-role-list" aria-label={t('permissions.roles', { defaultValue: 'Roles' })}>
        {visible.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              className={`perm-role ${r.id === selectedId ? 'on' : ''}`}
              aria-current={r.id === selectedId ? 'true' : undefined}
              disabled={disabled}
              onClick={() => r.id !== selectedId && onSelect(r.id)}
            >
              <span className="perm-avatar" style={{ background: roleColor(r.code) }}>{initials(r.name)}</span>
              <span className="perm-role-text">
                <span className="perm-role-name">
                  {r.name}
                  {dirtyRoleId === r.id && <span className="perm-dot" title={t('permissions.unsaved', { defaultValue: 'Unsaved changes' })} />}
                </span>
                <span className="perm-role-meta">
                  {t('permissions.memberCount', { count: r.memberCount ?? 0, defaultValue: '{{count}} members' })}
                </span>
              </span>
              {r.isFullAccess && <span className="perm-badge full">{t('permissions.full', { defaultValue: 'Full' })}</span>}
              {r.isSystem && <span className="perm-badge sys">{t('permissions.system', { defaultValue: 'System' })}</span>}
            </button>
          </li>
        ))}
        {visible.length === 0 && <li className="perm-empty-small">{t('permissions.noRolesMatch', { defaultValue: 'No roles match.' })}</li>}
      </ul>
      {canCreate && (
        <button type="button" className="perm-btn block" disabled={disabled} onClick={onCreate}>
          + {t('permissions.newRole', { defaultValue: 'New role' })}
        </button>
      )}
    </aside>
  );
}

export function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
